<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConvexQuery, useConvexClient } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useDateFormatter } from '../composables/useDateFormatter';
import ConfirmDialog from './ConfirmDialog.vue';

const { t } = useI18n();
const { formatDate } = useDateFormatter();

type WorkshopRole = 'owner' | 'member' | 'reader';

interface Member {
  id: string;
  name: string;
  email: string;
  role: WorkshopRole;
  joinedAt: number;
}

const props = defineProps<{
  modelValue: boolean;
  workshopId: Id<'workshops'>;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const client = useConvexClient();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const newLinkRole = ref<WorkshopRole>('member');
const isCreating = ref(false);
const createError = ref('');
const copiedToken = ref<string | null>(null);
const showRevokeConfirm = ref(false);
const revokeToken = ref<string | null>(null);
const showRemoveConfirm = ref(false);
const showSelfDemoteConfirm = ref(false);
const memberToRemove = ref<Member | null>(null);
const pendingSelfDemote = ref<{ member: Member; newRole: WorkshopRole } | null>(null);
const updatingMemberId = ref<string | null>(null);
const updateError = ref('');
const removeError = ref('');

const { data: inviteLinks, isPending: linksLoading } = useConvexQuery(
  api.inviteLinks.getForWorkshop,
  () => ({ workshopId: props.workshopId })
);

const { data: members, isPending: membersLoading } = useConvexQuery(
  api.workshops.getMembers,
  () => ({ workshopId: props.workshopId })
);

const { data: currentUser } = useConvexQuery(api.auth.currentUser, () => ({}));

const safeMembers = computed(() => {
  return ((members.value as Array<Member | null> | undefined) || []).filter(
    (member): member is Member => Boolean(member)
  );
});

const currentUserId = computed(() => currentUser.value?.userId);
const ownerCount = computed(
  () => safeMembers.value.filter(member => member.role === 'owner').length
);

const activeLinks = computed(() => {
  if (!inviteLinks.value) return [];
  return (inviteLinks.value as any[]).filter(link => link.isActive);
});

const roleLabels: Record<WorkshopRole, string> = {
  owner: t('roles.owner'),
  member: t('roles.member'),
  reader: t('roles.reader'),
};

const roleDescriptions: Record<WorkshopRole, string> = {
  owner: t('roles.ownerDescription'),
  member: t('roles.memberDescription'),
  reader: t('roles.readerDescription'),
};

const roleColors: Record<WorkshopRole, string> = {
  owner: 'bg-(--color-postit-pink)',
  member: 'bg-(--color-postit-blue)',
  reader: 'bg-(--color-postit-green)',
};

const getInviteUrl = (token: string) => {
  const baseUrl = window.location.origin;
  return baseUrl + '/login?invite=' + token;
};

const handleCreateLink = async () => {
  isCreating.value = true;
  createError.value = '';
  try {
    await client.mutation(api.inviteLinks.create, {
      workshopId: props.workshopId,
      role: newLinkRole.value,
    });
    newLinkRole.value = 'member';
  } catch (e: any) {
    createError.value = e.message || t('access.failedToCreate');
  } finally {
    isCreating.value = false;
  }
};

const handleRevokeLink = async (token: string) => {
  revokeToken.value = token;
  showRevokeConfirm.value = true;
};

const confirmRevoke = async () => {
  if (!revokeToken.value) return;
  try {
    await client.mutation(api.inviteLinks.revoke, {
      token: revokeToken.value,
    });
    showRevokeConfirm.value = false;
    revokeToken.value = null;
  } catch (e: any) {
    console.error('Failed to revoke link:', e);
  }
};

const cancelRevoke = () => {
  showRevokeConfirm.value = false;
  revokeToken.value = null;
};

const copyToClipboard = async (token: string) => {
  const url = getInviteUrl(token);
  try {
    await navigator.clipboard.writeText(url);
    copiedToken.value = token;
    setTimeout(() => {
      if (copiedToken.value === token) {
        copiedToken.value = null;
      }
    }, 2000);
  } catch (e) {
    console.error('Failed to copy:', e);
  }
};

const handleRemoveMember = (member: Member) => {
  removeError.value = '';
  memberToRemove.value = member;
  showRemoveConfirm.value = true;
};

const confirmRemoveMember = async () => {
  if (!memberToRemove.value) return;
  try {
    await client.mutation(api.workshops.kickMember, {
      workshopId: props.workshopId,
      userId: memberToRemove.value.id as Id<'users'>,
    });
    showRemoveConfirm.value = false;
    memberToRemove.value = null;
  } catch (e: any) {
    console.error('Failed to remove member:', e);
    removeError.value = e.message || t('access.failedToRemove');
  }
};

const cancelRemoveMember = () => {
  showRemoveConfirm.value = false;
  memberToRemove.value = null;
};

const handleRoleChange = async (member: Member, event: Event) => {
  const selectEl = event.target as HTMLSelectElement;
  const newRole = selectEl.value as WorkshopRole;
  if (member.role === newRole) return;

  const isSelf = member.id === currentUserId.value;
  const isSelfOwnerDemote = isSelf && member.role === 'owner' && newRole !== 'owner';

  if (isSelfOwnerDemote) {
    selectEl.value = member.role;
    pendingSelfDemote.value = { member, newRole };
    showSelfDemoteConfirm.value = true;
    return;
  }

  updatingMemberId.value = member.id;
  updateError.value = '';

  try {
    await client.mutation(api.workshops.updateMemberRole, {
      workshopId: props.workshopId,
      userId: member.id as Id<'users'>,
      newRole,
    });
  } catch (e: any) {
    console.error('Failed to update role:', e);
    updateError.value = e.message || t('access.failedToUpdate');
  } finally {
    updatingMemberId.value = null;
  }
};

const confirmSelfDemote = async () => {
  if (!pendingSelfDemote.value) return;

  updatingMemberId.value = pendingSelfDemote.value.member.id;
  updateError.value = '';

  try {
    await client.mutation(api.workshops.updateMemberRole, {
      workshopId: props.workshopId,
      userId: pendingSelfDemote.value.member.id as Id<'users'>,
      newRole: pendingSelfDemote.value.newRole,
    });
    showSelfDemoteConfirm.value = false;
    pendingSelfDemote.value = null;
  } catch (e: any) {
    console.error('Failed to update role:', e);
    updateError.value = e.message || 'Failed to update role';
  } finally {
    updatingMemberId.value = null;
  }
};

const cancelSelfDemote = () => {
  showSelfDemoteConfirm.value = false;
  pendingSelfDemote.value = null;
};

const close = () => {
  isOpen.value = false;
};

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    close();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="close"
    >
      <div class="absolute inset-0 bg-black/50" @click="close"></div>

      <div
        class="relative bg-[#d4c4a8] rounded-lg w-full max-w-4xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden"
        style="
          background-image: url(&quot;data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='corkNoise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.6' numOctaves='5' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23corkNoise)'/%3E%3C/svg%3E&quot;);
        "
      >
        <div class="absolute inset-0 bg-black/5 pointer-events-none"></div>

        <div class="p-6 border-b border-black/10 relative z-10 bg-[#c9b896]/80">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-bold text-gray-800">{{ $t('access.title') }}</h2>
            <button
              @click="close"
              class="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p class="text-gray-700 text-sm mt-1">{{ $t('access.subtitle') }}</p>
        </div>

        <div class="flex-1 overflow-y-auto p-6 space-y-6 relative z-10">
          <div>
            <h3 class="text-gray-800 font-semibold mb-4 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
                />
              </svg>
              {{ $t('access.members') }}
            </h3>

            <div v-if="membersLoading" class="text-center py-4">
              <p class="text-gray-600">{{ $t('access.loadingMembers') }}</p>
            </div>

            <div v-else-if="safeMembers.length === 0" class="text-center py-4 text-gray-600">
              {{ $t('access.noMembers') }}
            </div>

            <div v-else class="space-y-3 max-h-[40vh] overflow-y-auto">
              <div
                class="hidden md:block rounded-lg border border-black/10 bg-white/80 overflow-hidden"
              >
                <div
                  class="grid grid-cols-[1.4fr_1.2fr_0.8fr_0.9fr_0.7fr] gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-black/5"
                >
                  <span>{{ $t('access.name') }}</span>
                  <span>{{ $t('access.email') }}</span>
                  <span>{{ $t('access.role') }}</span>
                  <span>{{ $t('access.joined') }}</span>
                  <span class="text-right">{{ $t('access.actions') }}</span>
                </div>
                <div
                  v-for="member in safeMembers"
                  :key="member.id"
                  class="grid grid-cols-[1.4fr_1.2fr_0.8fr_0.9fr_0.7fr] gap-3 px-4 py-2 text-sm border-t border-black/10 items-center"
                >
                  <span class="font-medium text-gray-800 truncate">{{ member.name }}</span>
                  <span class="text-gray-600 break-all">{{ member.email }}</span>
                  <span class="inline-flex items-center">
                    <span
                      class="px-2 py-0.5 rounded text-xs font-medium"
                      :class="roleColors[member.role]"
                    >
                      {{ roleLabels[member.role] }}
                    </span>
                  </span>
                  <span class="text-gray-500 text-xs">{{ formatDate(member.joinedAt) }}</span>
                  <span class="flex items-center justify-end gap-2">
                    <select
                      :value="member.role"
                      @change="handleRoleChange(member, $event)"
                      :disabled="
                        updatingMemberId === member.id ||
                        (member.id === currentUserId && member.role === 'owner' && ownerCount <= 1)
                      "
                      class="px-2 py-1 bg-white border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                    >
                      <option value="owner">Owner</option>
                      <option value="member">Member</option>
                      <option value="reader">Reader</option>
                    </select>

                    <button
                      @click="handleRemoveMember(member)"
                      class="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                      :class="member.role === 'owner' ? 'opacity-0 pointer-events-none' : ''"
                      :aria-hidden="member.role === 'owner'"
                      :title="$t('access.remove')"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                </div>
              </div>

              <div class="md:hidden space-y-3">
                <div
                  v-for="member in safeMembers"
                  :key="member.id"
                  class="rounded-lg border border-black/10 bg-white/90 p-3"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <div class="font-semibold text-gray-800 truncate">{{ member.name }}</div>
                      <div class="text-gray-600 break-all text-sm">{{ member.email }}</div>
                    </div>
                    <span
                      class="px-2 py-0.5 rounded text-xs font-medium shrink-0"
                      :class="roleColors[member.role]"
                    >
                      {{ roleLabels[member.role] }}
                    </span>
                  </div>
                  <div class="mt-2 text-xs text-gray-500">
                    {{ $t('access.joined') }} {{ formatDate(member.joinedAt) }}
                  </div>
                  <div class="mt-3 flex items-center justify-between gap-2">
                    <select
                      :value="member.role"
                      @change="handleRoleChange(member, $event)"
                      :disabled="
                        updatingMemberId === member.id ||
                        (member.id === currentUserId && member.role === 'owner' && ownerCount <= 1)
                      "
                      class="px-2 py-1 bg-white border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                    >
                      <option value="owner">{{ $t('roles.owner') }}</option>
                      <option value="member">{{ $t('roles.member') }}</option>
                      <option value="reader">{{ $t('roles.reader') }}</option>
                    </select>

                    <button
                      @click="handleRemoveMember(member)"
                      class="px-3 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors"
                      :class="member.role === 'owner' ? 'opacity-0 pointer-events-none' : ''"
                      :aria-hidden="member.role === 'owner'"
                    >
                      {{ $t('access.remove') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="updateError" class="mt-3 text-red-600 text-sm">{{ updateError }}</div>
            <div v-if="removeError" class="mt-2 text-red-600 text-sm">{{ removeError }}</div>
          </div>

          <hr class="border-black/10" />

          <div
            class="paper-card cork-pin p-5 rounded-sm bg-white"
            :style="{ '--card-rotation': '-0.5deg' }"
          >
            <div class="paper-texture rounded-sm"></div>

            <div class="relative z-10 space-y-4">
              <h3 class="text-gray-800 font-semibold flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"
                  />
                </svg>
                {{ $t('access.createInviteLink') }}
              </h3>

              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="role in ['reader', 'member', 'owner'] as WorkshopRole[]"
                  :key="role"
                  @click="newLinkRole = role"
                  class="px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer border-2"
                  :class="
                    newLinkRole === role
                      ? 'border-gray-800 bg-gray-800 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  "
                >
                  {{ roleLabels[role] }}
                </button>
              </div>

              <p class="text-gray-600 text-sm">{{ roleDescriptions[newLinkRole] }}</p>

              <button
                @click="handleCreateLink"
                :disabled="isCreating"
                class="push-button w-full px-4 py-3 rounded-lg"
              >
                {{ isCreating ? $t('access.creating') : $t('access.generateInviteLink') }}
              </button>

              <div v-if="createError" class="text-red-600 text-sm">{{ createError }}</div>
            </div>
          </div>

          <hr class="border-black/10" />

          <div>
            <h3 class="text-gray-800 font-semibold mb-4">{{ $t('access.activeInviteLinks') }}</h3>

            <div v-if="linksLoading" class="text-center py-4">
              <p class="text-gray-600">{{ $t('access.loading') }}</p>
            </div>

            <div v-else-if="activeLinks.length === 0" class="text-center py-4 text-gray-600">
              {{ $t('access.noActiveLinks') }}
            </div>

            <div v-else class="space-y-3">
              <div
                class="hidden md:block rounded-lg border border-black/10 bg-white/80 overflow-hidden"
              >
                <div
                  class="grid grid-cols-[0.7fr_1.6fr_0.7fr_0.9fr] gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-black/5"
                >
                  <span>{{ $t('access.role') }}</span>
                  <span>{{ $t('access.inviteLink') }}</span>
                  <span>{{ $t('access.created') }}</span>
                  <span class="text-right">{{ $t('access.actions') }}</span>
                </div>
                <div
                  v-for="link in activeLinks"
                  :key="link.token"
                  class="grid grid-cols-[0.7fr_1.6fr_0.7fr_0.9fr] gap-3 px-4 py-2 text-sm border-t border-black/10 items-center"
                >
                  <span class="inline-flex items-center">
                    <span
                      class="px-2 py-0.5 rounded text-xs font-medium"
                      :class="roleColors[link.role as WorkshopRole]"
                    >
                      {{ roleLabels[link.role as WorkshopRole] }}
                    </span>
                  </span>
                  <code class="bg-black/10 rounded px-2 py-1 text-xs text-gray-700 truncate">
                    {{ getInviteUrl(link.token) }}
                  </code>
                  <span class="text-gray-600 text-xs">{{ formatDate(link.createdAt) }}</span>
                  <span class="flex items-center justify-end gap-2">
                    <button
                      @click="copyToClipboard(link.token)"
                      class="px-3 py-1.5 bg-gray-800 text-white rounded text-xs hover:bg-gray-700 transition-colors"
                      :class="{ 'bg-green-600': copiedToken === link.token }"
                    >
                      {{ copiedToken === link.token ? $t('access.copied') : $t('access.copy') }}
                    </button>

                    <button
                      @click="handleRevokeLink(link.token)"
                      class="px-3 py-1.5 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors"
                    >
                      {{ $t('access.revoke') }}
                    </button>
                  </span>
                </div>
              </div>

              <div class="md:hidden space-y-3">
                <div
                  v-for="link in activeLinks"
                  :key="link.token"
                  class="rounded-lg border border-black/10 bg-white/90 p-3"
                >
                  <div class="flex items-start justify-between gap-3">
                    <span
                      class="px-2 py-0.5 rounded text-xs font-medium shrink-0"
                      :class="roleColors[link.role as WorkshopRole]"
                    >
                      {{ roleLabels[link.role as WorkshopRole] }}
                    </span>
                    <span class="text-xs text-gray-500">{{ formatDate(link.createdAt) }}</span>
                  </div>
                  <code
                    class="mt-2 block bg-black/10 rounded px-2 py-1 text-xs text-gray-700 break-all"
                  >
                    {{ getInviteUrl(link.token) }}
                  </code>
                  <div class="mt-3 flex items-center justify-end gap-2">
                    <button
                      @click="copyToClipboard(link.token)"
                      class="px-3 py-1.5 bg-gray-800 text-white rounded text-xs hover:bg-gray-700 transition-colors"
                      :class="{ 'bg-green-600': copiedToken === link.token }"
                    >
                      {{ copiedToken === link.token ? $t('access.copied') : $t('access.copy') }}
                    </button>

                    <button
                      @click="handleRevokeLink(link.token)"
                      class="px-3 py-1.5 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors"
                    >
                      {{ $t('access.revoke') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmDialog
      v-model="showRevokeConfirm"
      :title="$t('access.revokeConfirmTitle')"
      :message="$t('access.revokeConfirmMessage')"
      :confirm-text="$t('access.revoke')"
      :cancel-text="$t('common.cancel')"
      confirm-color="destructive"
      @confirm="confirmRevoke"
      @cancel="cancelRevoke"
    />

    <ConfirmDialog
      v-model="showRemoveConfirm"
      :title="$t('access.removeMemberTitle')"
      :message="
        memberToRemove
          ? $t('access.removeMemberMessage', { name: memberToRemove.name })
          : $t('access.removeMemberTitle')
      "
      :confirm-text="$t('access.remove')"
      :cancel-text="$t('common.cancel')"
      confirm-color="destructive"
      @confirm="confirmRemoveMember"
      @cancel="cancelRemoveMember"
    />

    <ConfirmDialog
      v-model="showSelfDemoteConfirm"
      :title="$t('access.changeRoleTitle')"
      :message="
        pendingSelfDemote
          ? $t('access.changeRoleMessage', { role: roleLabels[pendingSelfDemote.newRole] })
          : $t('access.changeRoleTitle')
      "
      :confirm-text="$t('common.continue')"
      :cancel-text="$t('common.cancel')"
      confirm-color="destructive"
      @confirm="confirmSelfDemote"
      @cancel="cancelSelfDemote"
    />
  </Teleport>
</template>
